import { useMemo } from "react";
import { deskLineKey, removedDeskLines } from "../../lib/deskWrites";

const BLOCK_SELECTOR = "p, h1, h2, h3, h4, h5, h6, li, blockquote";
const UNSAFE_SELECTOR = "script, style, iframe, object, embed, link, meta";

/**
 * Lumen's new version of a doc, shown before the user picks "Update doc" or "Keep both".
 * Lines that are new or changed compared with the current doc are highlighted; lines the new
 * version drops are listed underneath.
 */
export default function DeskVersionPreview({ currentHtml, draftHtml, onDark = false, maxHeight = 220 }) {
  const { html, changedCount } = useMemo(() => {
    const doc = new DOMParser().parseFromString(`<div>${draftHtml || ""}</div>`, "text/html");
    const root = doc.body.firstChild;
    root.querySelectorAll(UNSAFE_SELECTOR).forEach((el) => el.remove());
    root.querySelectorAll("*").forEach((el) => {
      [...el.attributes].forEach((attr) => {
        if (/^on/i.test(attr.name) || /^\s*javascript:/i.test(attr.value)) el.removeAttribute(attr.name);
      });
    });

    const currentKeys = new Set(
      [...new DOMParser().parseFromString(`<div>${currentHtml || ""}</div>`, "text/html").body.querySelectorAll(BLOCK_SELECTOR)]
        .filter((el) => !el.querySelector(BLOCK_SELECTOR))
        .map((el) => deskLineKey(el.textContent))
    );
    let changed = 0;
    root.querySelectorAll(BLOCK_SELECTOR).forEach((el) => {
      if (el.querySelector(BLOCK_SELECTOR)) return;
      const key = deskLineKey(el.textContent);
      if (key && !currentKeys.has(key)) {
        el.setAttribute("data-changed", "true");
        changed += 1;
      }
    });
    return { html: root.innerHTML, changedCount: changed };
  }, [currentHtml, draftHtml]);

  const removed = useMemo(() => removedDeskLines(currentHtml, draftHtml), [currentHtml, draftHtml]);
  const highlight = onDark ? "rgba(125,216,213,0.18)" : "rgba(77,184,181,0.16)";
  const muted = onDark ? "rgba(255,255,255,0.55)" : "#64748B";

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", whiteSpace: "normal" }}>
      <div style={{ fontSize: 11, color: muted, marginBottom: 4 }}>
        {changedCount ? "Changed lines are highlighted." : "No lines changed."}
      </div>
      <div
        data-testid="desk-version-preview"
        className="desk-version-preview"
        style={{
          maxHeight, overflowY: "auto", fontSize: 12, lineHeight: 1.55, padding: "8px 10px", borderRadius: 8,
          background: onDark ? "rgba(255,255,255,0.05)" : "#F8FAFC",
          border: `1px solid ${onDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)"}`,
          color: onDark ? "rgba(255,255,255,0.85)" : "#1E293B",
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {removed.length > 0 && (
        <div style={{ fontSize: 11, color: muted, marginTop: 6 }}>
          Removed:
          {removed.map((line) => (
            <div key={line} style={{ textDecoration: "line-through", opacity: 0.8 }}>{line}</div>
          ))}
        </div>
      )}
      <style>{`
        .desk-version-preview h1, .desk-version-preview h2 { font-size: 13px; font-weight: 700; margin: 8px 0 4px; }
        .desk-version-preview h3 { font-size: 12px; font-weight: 700; margin: 6px 0 3px; }
        .desk-version-preview p { margin: 0 0 6px; }
        .desk-version-preview ul, .desk-version-preview ol { padding-left: 18px; margin: 0 0 6px; }
        .desk-version-preview ul { list-style: disc; }
        .desk-version-preview ol { list-style: decimal; }
        .desk-version-preview li p { margin: 0; }
        .desk-version-preview [data-changed] { background: ${highlight}; border-radius: 4px; }
      `}</style>
    </div>
  );
}
